import { useState } from "react";
import { Truck, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import {
  setComplianceContext,
  validateShipment,
  validateEcmr,
} from "../../services/compliance-api";
import type { ValidationResponse } from "../../types/compliance";

interface Props {
  workflowId: string;
  tenantId?: string;
  onComplete?: (result: ValidationResponse) => void;
}

const EU_COUNTRIES = [
  "DE", "NL", "FR", "BE", "GB", "PL", "IT", "ES", "AT", "CH",
  "DK", "SE", "CZ", "HU", "RO", "BG", "GR", "PT", "IE", "FI",
];

type Tab = "shipment" | "ecmr";

export default function ShipmentValidationPanel({
  workflowId,
  tenantId = "default",
  onComplete,
}: Props) {
  const [tab, setTab] = useState<Tab>("shipment");

  // Shipment fields
  const [trackingNumber, setTrackingNumber] = useState("");
  const [originCountry, setOriginCountry] = useState("DE");
  const [destinationCountry, setDestinationCountry] = useState("FR");
  const [customsDeclared, setCustomsDeclared] = useState(true);
  const [incoterm, setIncoterm] = useState("");

  // eCMR fields
  const [sender, setSender] = useState("");
  const [receiver, setReceiver] = useState("");
  const [carrier, setCarrier] = useState("");
  const [goodsDescription, setGoodsDescription] = useState("");
  const [weightKg, setWeightKg] = useState("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ValidationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleShipmentValidate() {
    if (!trackingNumber.trim()) {
      setError("Tracking number is required.");
      return;
    }
    setComplianceContext(tenantId, "SHIPMENT_OPERATOR");
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await validateShipment({
        tenant_id: tenantId,
        workflow_id: workflowId,
        tracking_number: trackingNumber.trim(),
        origin_country: originCountry,
        destination_country: destinationCountry,
        metadata: {
          customs_declared: customsDeclared,
          incoterm: incoterm || undefined,
        },
      });
      setResult(res);
      onComplete?.(res);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Shipment validation failed",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleEcmrValidate() {
    if (!sender.trim() || !receiver.trim() || !carrier.trim()) {
      setError("Sender, receiver, and carrier are required for eCMR.");
      return;
    }
    setComplianceContext(tenantId, "SHIPMENT_OPERATOR");
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await validateEcmr({
        tenant_id: tenantId,
        workflow_id: workflowId,
        document_id: `ecmr_${Date.now()}`,
        sender: sender.trim(),
        receiver: receiver.trim(),
        carrier: carrier.trim(),
        goods_description: goodsDescription.trim() || undefined,
        weight_kg: parseFloat(weightKg) || undefined,
        metadata: {},
      });
      setResult(res);
      onComplete?.(res);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "eCMR validation failed",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Truck size={14} className="text-orange-600" />
        Shipment Validation
      </h3>

      {/* Tabs */}
      <div className="flex gap-1 mb-3 bg-gray-100 rounded-lg p-0.5">
        <button
          type="button"
          onClick={() => {
            setTab("shipment");
            setResult(null);
            setError(null);
          }}
          className={`flex-1 text-xs font-medium py-1.5 rounded-md transition ${
            tab === "shipment"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Cross-Border
        </button>
        <button
          type="button"
          onClick={() => {
            setTab("ecmr");
            setResult(null);
            setError(null);
          }}
          className={`flex-1 text-xs font-medium py-1.5 rounded-md transition ${
            tab === "ecmr"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          eCMR
        </button>
      </div>

      <div className="space-y-2">
        {tab === "shipment" ? (
          <>
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Tracking number"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
            />
            <div className="flex gap-2">
              <select
                value={originCountry}
                onChange={(e) => setOriginCountry(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
              >
                {EU_COUNTRIES.map((cc) => (
                  <option key={cc} value={cc}>
                    {cc}
                  </option>
                ))}
              </select>
              <span className="text-gray-400 self-center text-xs">→</span>
              <select
                value={destinationCountry}
                onChange={(e) => setDestinationCountry(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
              >
                {EU_COUNTRIES.map((cc) => (
                  <option key={cc} value={cc}>
                    {cc}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={customsDeclared}
                  onChange={(e) => setCustomsDeclared(e.target.checked)}
                />
                Customs declared
              </label>
            </div>
            <input
              type="text"
              value={incoterm}
              onChange={(e) => setIncoterm(e.target.value)}
              placeholder="Incoterm (e.g. DAP, FOB, CIF)"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
            />
            <button
              type="button"
              onClick={() => void handleShipmentValidate()}
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white text-sm py-1.5 rounded-lg flex items-center justify-center gap-2 transition"
            >
              {loading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Truck size={14} />
              )}
              {loading ? "Validating…" : "Validate Shipment"}
            </button>
          </>
        ) : (
          <>
            <input
              type="text"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder="Sender (consignor)"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              value={receiver}
              onChange={(e) => setReceiver(e.target.value)}
              placeholder="Receiver (consignee) *"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="Carrier *"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              value={goodsDescription}
              onChange={(e) => setGoodsDescription(e.target.value)}
              placeholder="Goods description"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="number"
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
              placeholder="Weight (kg)"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"
            />
            <button
              type="button"
              onClick={() => void handleEcmrValidate()}
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white text-sm py-1.5 rounded-lg flex items-center justify-center gap-2 transition"
            >
              {loading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Truck size={14} />
              )}
              {loading ? "Validating…" : "Validate eCMR"}
            </button>
          </>
        )}

        {error && (
          <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">
            <AlertCircle size={12} className="shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {result && (
          <div
            className={`text-xs px-3 py-2 rounded-lg border ${
              result.passed
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              {result.passed ? (
                <CheckCircle size={12} />
              ) : (
                <AlertCircle size={12} />
              )}
              <span className="font-semibold">
                {result.passed ? "Passed" : "Failed"}
              </span>
              <span className="ml-auto font-mono">
                {(result.score * 100).toFixed(0)}%
              </span>
            </div>
            <p>{result.message}</p>
            {result.findings.length > 0 && (
              <ul className="list-disc list-inside mt-1 opacity-80">
                {result.findings.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
